import { ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { type PlaceFeature } from "~/lib/api";
import { cn } from "~/lib/utils";
import { Icon } from "~/components/ui/icons/icon";
import { PlaceTypeIcon } from "~/components/ui/icons/place-type-icons";

interface PlaceListProps {
  places: PlaceFeature[];
  selectedId: string | null;
  emptyState?: ReactNode;
}

const defaultEmptyState = (
  <div className="px-4 pt-4 pb-10 text-center text-muted-foreground">
    <p className="text-sm">No places yet</p>
    <p className="text-xs mt-1 inline-flex items-center gap-1">
      Click <Icon i="add-place" className="size-4" /> to add one
    </p>
  </div>
);

export default function PlaceList({ places, selectedId, emptyState = defaultEmptyState }: PlaceListProps) {
  const [searchParams] = useSearchParams();

  if (places.length === 0) {
    return <>{emptyState}</>;
  }

  return (
    <div className="divide-y divide-border">
      {places.map((place) => {
        const placeId = place.id as string;
        const linkParams = new URLSearchParams(searchParams);
        linkParams.set("place", placeId);

        return (
          <Link
            key={placeId}
            to={`?${linkParams.toString()}`}
            className={cn(
              "w-full flex items-center gap-3 p-3 text-left transition-colors",
              selectedId === placeId ? "bg-muted text-muted-foreground" : "hover:bg-accent",
            )}
          >
            <span
              className={cn("size-8 rounded-full flex items-center justify-center shrink-0", {
                "bg-primary": selectedId === placeId,
                "bg-secondary": selectedId !== placeId,
              })}
            >
              <PlaceTypeIcon
                type={place.properties.type}
                className={cn("size-5", {
                  "text-primary-foreground": selectedId === placeId,
                  "text-secondary-foreground": selectedId !== placeId,
                })}
              />
            </span>
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{place.properties.name}</div>
              {place.properties.description && (
                <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{place.properties.description}</div>
              )}
            </div>
          </Link>
        );
      })}
    </div>
  );
}
